/* catalogIds.js — STABLE IDENTITY for technician-library references
 * (2026-08-22 user mandate: "not everything has its own index and its own
 * logic — fully fix that"; catalog-master-audit P0 #4 "index-id fragility").
 *
 * THE PROBLEM: customer configs stored ARRAY-INDEX ids ("carc-K", "gola-K",
 * "gc-K", "hc-K", "pgN-cK", "pmc-mi-ci", keyless "plinth-K") into the
 * technician blob's arrays — deleting or reordering a library entry silently
 * re-pointed every saved kitchen to the WRONG entry.
 *
 * THE FIX (audit prescription — slugs + index read-aliases):
 *  · stampUids(blob): every entry of the positional libraries gets a
 *    persistent numeric `uid` (blob._uidSeq counter, stamped once, saved with
 *    the blob, NEVER re-issued — deletes retire their uid forever). Runs on
 *    normalize AND after every store mutation, so new entries are always
 *    stamped.
 *  · new ids are minted in UID form: "carc-u7", "gola-u3", "pgN-cu5",
 *    "pmc-u2-u9" — immune to library edits.
 *  · every resolver accepts BOTH forms (resolveTok): "u<uid>" finds the entry
 *    by uid wherever it moved; a bare index keeps resolving positionally so
 *    every legacy save behaves exactly as before.
 *  · mirrored in the backend (pricing_system_a.py / catalog_rules.py — JS≡PY).
 *
 * Pure helpers, zero deps. Blobs without uids (tests, legacy) mint index ids
 * — byte-identical legacy behavior until a blob is stamped. */

/** The positional libraries that carry uids. pmat[].colors and
 *  groups[].doors are nested — stamped by the same pass. */
export function stampUids(blob) {
  if (!blob || typeof blob !== "object") return blob;
  let seq = Number.isFinite(Number(blob._uidSeq)) ? Number(blob._uidSeq) : 0;
  const stamp = (list) => {
    (Array.isArray(list) ? list : []).forEach((e) => {
      if (e && typeof e === "object" && e.uid == null) e.uid = ++seq;
    });
  };
  stamp(blob.colors);
  stamp(blob.gola);
  stamp(blob.sidePanels);
  stamp(blob.sidePanelColors);
  stamp(blob.golaColors);
  stamp(blob.handleColors);
  stamp(blob.pmat);
  (blob.pmat || []).forEach((m) => stamp(m && m.colors));
  (blob.groups || []).forEach((g) => stamp(g && g.doors));
  if (seq !== Number(blob._uidSeq || 0)) blob._uidSeq = seq;
  return blob;
}

/** tokFor(entry, idx) → "u<uid>" when the entry is stamped, else "<idx>"
 *  (legacy form — keeps un-stamped blobs, incl. every test fixture,
 *  byte-identical). */
export function tokFor(entry, idx) {
  return entry && entry.uid != null ? "u" + entry.uid : String(idx);
}

/** resolveTok(list, tok) → index into list, or −1.
 *  tok "u<uid>" → the entry carrying that uid (wherever it moved);
 *  tok "<K>"    → K when in range (the legacy positional read-alias). */
export function resolveTok(list, tok) {
  const L = Array.isArray(list) ? list : [];
  const m = /^(u)?(\d+)$/.exec(String(tok == null ? "" : tok));
  if (!m) return -1;
  if (m[1]) {
    const uid = +m[2];
    for (let i = 0; i < L.length; i++) if (L[i] && L[i].uid === uid) return i;
    return -1;
  }
  const k = +m[2];
  return k >= 0 && k < L.length ? k : -1;
}

/** refFor(prefix, entry, idx) → "<prefix>-u7" | "<prefix>-3" */
export function refFor(prefix, entry, idx) { return prefix + "-" + tokFor(entry, idx); }

/** resolveIdx(list, ref, prefix) → index for a "<prefix>-<tok>" ref, or −1. */
export function resolveIdx(list, ref, prefix) {
  const s = String(ref || "");
  if (s.indexOf(prefix + "-") !== 0) return -1;
  return resolveTok(list, s.slice(prefix.length + 1));
}

/** resolveEntry(list, ref, prefix) → the entry, or null. */
export function resolveEntry(list, ref, prefix) {
  const i = resolveIdx(list, ref, prefix);
  return i >= 0 ? (list || [])[i] : null;
}
